import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant-new/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantNew.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 *
 * Numbers in prose use Latin digits, as `lib/format.ts` does everywhere else.
 */
export const tenantNew = {
  'tenantNew.title': 'منشأة جديدة',
  'tenantNew.subtitle': 'افتح منشأة لعميل وادعُ مشرفها العام الأول. لا تمرّ كلمة مروره بهذه اللوحة إلا إن اخترت ذلك صراحةً.',

  /* Steps */
  'tenantNew.stepIdentity': 'هوية المنشأة',
  'tenantNew.stepAdmin': 'المشرف العام الأول',
  'tenantNew.stepReview': 'مراجعة',
  'tenantNew.stepResult': 'النتيجة',
  'tenantNew.next': 'التالي',
  'tenantNew.fixFields': 'صحّح الحقول المعلَّمة للمتابعة.',

  /* Step 1 — identity */
  'tenantNew.identityLead': 'ما يعرّف المنشأة على المنصّة. المعرّف لا يتغيّر بعد الإنشاء، فاختره بعناية.',
  'tenantNew.name': 'اسم المنشأة',
  'tenantNew.nameHint': 'كما يظهر في اللوحة وفي رسائل البريد إلى أعضائها.',
  'tenantNew.slug': 'المعرّف',
  'tenantNew.slugHint':
    'يدخل به أعضاء المنشأة ويظهر في عناوينها، ولا يتغيّر بعد الإنشاء. من 3 إلى 48 حرفًا: حروف لاتينية صغيرة وأرقام، وشرطة واحدة بين الكلمات.',
  'tenantNew.slugChecking': 'جارٍ التحقق من توفّر المعرّف…',
  'tenantNew.slugAvailable': 'المعرّف متاح.',
  'tenantNew.slugUnknown': 'تعذّر التحقق من التوفّر الآن ({message}). سيُتحقّق منه عند الإنشاء.',
  'tenantNew.slugSuggested': 'مقترح من الحروف اللاتينية في الاسم. عدّله إن شئت.',
  'tenantNew.slugNoLatin': 'لا حروف لاتينية في الاسم يُقترح منها معرّف، فاكتبه بنفسك.',
  'tenantNew.vertical': 'القطاع',
  'tenantNew.verticalHint': 'الحزمة المتخصّصة التي تعمل بها المنشأة.',
  'tenantNew.verticalsEmpty': 'لا قطاعات متاحة على هذا النشر، فلا يمكن إنشاء منشأة الآن. القطاعات تُضاف على مستوى النشر لا من هذه اللوحة.',
  'tenantNew.frontendUrl': 'عنوان واجهة المنشأة',
  'tenantNew.frontendUrlHint':
    'عنوان http أو https لتطبيق العميل إن كان له تطبيقه. عليه تُبنى روابط الدعوة واستعادة كلمة المرور، ويُقبل أصله في CORS. ليس عنوان العودة بعد الدخول عبر Microsoft. إن تُرك فارغًا تُبنى الروابط على عنوان المنصّة العام.',
  'tenantNew.frontendAdviceTitle': 'ننصح بتعبئة عنوان الواجهة إن كان للعميل تطبيقه',
  'tenantNew.frontendAdvice':
    'يُحفظ مع المنشأة في الطلب نفسه، فيُبنى عليه رابط دعوة المشرف الأول من البداية. إن تُرك فارغًا بُني الرابط على عنوان المنصّة العام، وقد يقود إلى صفحة لا تخص العميل.',
  'tenantNew.frontendNoPermission':
    'ضبط عنوان الواجهة للمشرف الأعلى للمنصّة وحده، لأنه أساس روابط الدعوة. ستُبنى روابط الدعوة على عنوان المنصّة العام حتى يضبطه من تبويب «البريد».',

  /* Step 2 — first general admin */
  'tenantNew.adminLead':
    'أول حساب في المنشأة، بدور المشرف العام. يصله رابط دعوة لمرة واحدة ليختار كلمة مروره بنفسه، فلا يحمل أحد على المنصّة بيانات دخوله.',
  'tenantNew.adminName': 'اسم المشرف',
  'tenantNew.adminNameHint': 'كما سيظهر في قائمة الأعضاء وفي رسالة الدعوة.',
  'tenantNew.adminEmail': 'بريد المشرف',
  'tenantNew.adminEmailHint': 'إليه وحده تُرسل الدعوة، فتأكّد منه.',
  'tenantNew.advanced': 'خيار متقدّم: كلمة مرور افتتاحية بدل الدعوة',
  'tenantNew.advancedLead':
    'للعميل الذي لا يصله بريد المنصّة بعد: خادم بريده يحجب رسائلنا، أو تجري التهيئة في اجتماع حضوري. لا تستخدمه إلا عند الحاجة.',
  'tenantNew.invitationOnly':
    'يصل المشرفَ رابط دعوة على بريده. خيار كلمة المرور الافتتاحية بدل الدعوة متاح للمشرف الأعلى للمنصّة وحده.',
  'tenantNew.usePassword': 'إنشاء الحساب بكلمة مرور افتتاحية بدل الدعوة',
  'tenantNew.usePasswordHint': 'لن تُرسل دعوة. تختار أنت كلمة المرور وتبلّغها للمشرف بنفسك.',
  'tenantNew.passwordWarnTitle': 'ستمرّ بيانات دخول عبر المنصّة',
  'tenantNew.passwordWarn':
    'تبلّغ المشرف بكلمة المرور عبر قناة آمنة خارج اللوحة، ولن تُعرض مرة أخرى بعد الإنشاء. لا يستطيع الحساب فعل شيء سوى تغييرها عند أول دخول، وتغييرها يُنهي كل جلسة سابقة.',
  'tenantNew.password': 'كلمة المرور الافتتاحية',
  'tenantNew.passwordHint': 'جملة قصيرة يسهل إملاؤها أفضل من رموز يصعب نقلها.',
  'tenantNew.rules': 'شروط كلمة المرور الافتتاحية',
  'tenantNew.ruleMet': 'مستوفى:',
  'tenantNew.ruleUnmet': 'غير مستوفى:',
  'tenantNew.rule.length': '12 حرفًا على الأقل.',
  'tenantNew.rule.varied': 'ليست حرفًا مكرّرًا ولا تسلسلًا متّصلًا.',
  'tenantNew.rule.obvious': 'لا تقوم على كلمة مرور شائعة.',
  'tenantNew.rule.personal': 'لا تحتوي اسم المشرف ولا بريده ولا اسم المنشأة.',

  /* Step 3 — review */
  'tenantNew.reviewLead': 'راجع ما سيُنشأ. الإنشاء عملية واحدة، والمعرّف لا يتغيّر بعدها.',
  'tenantNew.editSection': 'تعديل {section}',
  'tenantNew.frontendNone': 'لا شيء — تشير الروابط إلى عنوان المنصّة العام',
  'tenantNew.signIn': 'طريقة الدخول',
  'tenantNew.signInInvitation': 'دعوة بالبريد — يختار كلمة مروره بنفسه',
  'tenantNew.signInPassword': 'كلمة مرور افتتاحية تبلّغه بها بنفسك',
  'tenantNew.passwordSet': 'محدّدة، ولا تُعرض',
  'tenantNew.whatHappens': 'ما الذي سيحدث',
  'tenantNew.happenCreate': 'تُنشأ المنشأة ومشرفها العام الأول في عملية واحدة.',
  'tenantNew.happenInvite':
    'يُنشأ حساب المشرف بحالة «مدعوّ» دون كلمة مرور، وتُصدر له دعوة لمرة واحدة توضع في طابور البريد. تمرّ الرسالة عبر خادم بريد المنصّة، لأن المنشأة لا خادم بريد لها بعد.',
  'tenantNew.happenFrontend': 'يُحفظ عنوان الواجهة مع المنشأة في العملية نفسها، فيُبنى عليه رابط الدعوة الأولى.',
  'tenantNew.happenNoFrontend': 'يشير رابط الدعوة إلى عنوان المنصّة العام.',
  'tenantNew.happenPassword': 'يُنشأ حساب المشرف مفعّلًا بكلمة المرور الافتتاحية ولا تُرسل دعوة. تبليغه بها مسؤوليتك.',
  'tenantNew.submit': 'إنشاء المنشأة',
  'tenantNew.submitFailed': 'لم تُنشأ المنشأة',
  'tenantNew.submitRetrySafe': 'إعادة المحاولة آمنة: الطلب نفسه لا يُنشئ منشأتين.',

  /* Result */
  'tenantNew.resultTitle': 'أُنشئت المنشأة',
  'tenantNew.resultLead': 'هذا ما حدث فعلًا، وما بقي ليبدأ العميل.',
  'tenantNew.howSignIn': 'كيف يدخل المشرف العام الأول',
  'tenantNew.queuedTitle': 'وُضعت الدعوة في طابور البريد',
  'tenantNew.queuedBody': 'سيصل إلى البريد أدناه رابط لمرة واحدة يختار به المشرف كلمة مروره.',
  'tenantNew.expires': 'ينتهي الرابط {date}.',
  'tenantNew.relayNote': 'تمرّ الرسالة عبر خادم بريد المنصّة، لأن المنشأة لا خادم بريد لها بعد.',
  'tenantNew.linkBase': 'يشير الرابط إلى:',
  'tenantNew.linkBasePlatform': 'عنوان المنصّة العام',
  'tenantNew.dispatch': 'حالة رسالة الدعوة',
  'tenantNew.dispatchWaiting': 'لم يلتقطها عامل البريد بعد.',
  'tenantNew.dispatchStillQueued': 'ما زالت في الطابور. سجل الإرسال في تبويب البريد يبيّن ما يحدث لها.',
  'tenantNew.dispatchError': 'السبب كما سجّله المُرحِّل:',
  'tenantNew.dispatchUnreadable': 'تعذّر قراءة سجل الإرسال: {message}',
  'tenantNew.relayFailedTitle': 'لم تصل الدعوة: رفضها خادم البريد',
  'tenantNew.relayFailedBody': 'المنشأة وحساب مشرفها موجودان، والرابط صدر، لكن المُرحِّل رفض الرسالة فلن تصل. السبب مسجّل أدناه.',
  'tenantNew.failedTitle': 'أُنشئت المنشأة، لكن الدعوة لم تُرسل',
  'tenantNew.failedBody': 'المنشأة وحساب مشرفها موجودان، ولا رابط في الطريق إليه. لن يتمكّن من الدخول حتى تُرسل الدعوة.',
  'tenantNew.failedNext':
    'إن كان خادم بريد المنصّة لا يصل إلى العميل فاضبط بريد المنشأة أولًا، ثم أعد إرسال الدعوة من هنا أو من تبويب الأعضاء.',
  'tenantNew.resend': 'إعادة إرسال الدعوة',
  'tenantNew.resent': 'وُضعت دعوة جديدة في الطابور، وأُبطل أي رابط سابق.',
  'tenantNew.resendFailed': 'تعذّرت إعادة الإرسال: {message}',
  'tenantNew.resendNoPermission': 'إعادة الإرسال تحتاج صلاحية إدارة المنشآت، وليست ضمن رتبتك.',
  'tenantNew.passwordTitle': 'الحساب مفعّل بكلمة المرور الافتتاحية',
  'tenantNew.passwordBody':
    'لم تُرسل دعوة. بلّغ المشرف بكلمة المرور عبر قناة آمنة خارج اللوحة؛ لن تُعرض مرة أخرى. أول ما يُطلب منه عند الدخول تغييرها.',
  'tenantNew.nextSteps': 'الخطوات التالية',
  'tenantNew.nextMail': 'خادم بريد المنشأة الخاص وعنوان الروابط. حتى يُضبط تمرّ رسائلها عبر خادم المنصّة.',
  'tenantNew.nextMicrosoft': 'ربط دليل Entra الخاص بالعميل ليدخل أعضاؤه بحساباتهم.',
  'tenantNew.nextTemplatesTitle': 'قوالب البريد',
  'tenantNew.nextOpen': 'افتح',
  'tenantNew.nextTemplates': 'نصوص الرسائل وهوية المنشأة فيها، ومنها رسالة الدعوة.',
  'tenantNew.nextMembers': 'حالة المشرف الأول، وإعادة الدعوة، وإضافة بقية الأعضاء.',
  'tenantNew.openTenant': 'فتح المنشأة',
  'tenantNew.createAnother': 'إنشاء منشأة أخرى',

  /* Leaving */
  'tenantNew.leaveTitle': 'مغادرة دون إنشاء؟',
  'tenantNew.leaveConsequence': 'ما كتبته في هذا المعالج سيُفقد. لم يُنشأ شيء بعد.',
  'tenantNew.leaveAffected': 'لا أحد — لا منشأة ولا حساب ولا دعوة.',
  'tenantNew.leaveConfirm': 'مغادرة',

  /* Validation — the server's rules, said before the round trip */
  'tenantNew.error.nameRequired': 'اكتب اسم المنشأة.',
  'tenantNew.error.nameTooLong': 'لا يزيد الاسم على {max} حرف.',
  'tenantNew.error.slugRequired': 'اختر معرّفًا للمنشأة.',
  'tenantNew.error.slugTaken': 'سبقتك منشأة أخرى إلى هذا المعرّف. اختر غيره.',
  'tenantNew.error.verticalRequired': 'اختر قطاع المنشأة.',
  'tenantNew.error.verticalUnknown': 'هذا القطاع لم يعد متاحًا. اختر من القائمة المحدّثة.',
  'tenantNew.error.urlTooLong': 'لا يزيد العنوان على {max} حرف.',
  'tenantNew.error.urlShape': 'اكتب عنوانًا كاملًا يبدأ بـ https://',
  'tenantNew.error.adminNameRequired': 'اكتب اسم المشرف.',
  'tenantNew.error.emailRequired': 'اكتب بريد المشرف.',
  'tenantNew.error.emailShape': 'هذا لا يبدو عنوان بريد صحيحًا.',
  'tenantNew.error.passwordRequired': 'اكتب كلمة المرور الافتتاحية، أو ألغِ هذا الخيار لتُرسل دعوة.',
  'tenantNew.error.passwordTooLong': 'لا تزيد كلمة المرور على {max} حرف.',
  'tenantNew.error.passwordWeak': 'كلمة المرور لا تستوفي الشروط أدناه.',
  'tenantNew.error.operatorAddress': 'هذا العنوان لمشغّل في المنصّة ولا يمكن أن يكون حساب عضو في منشأة.',
  'tenantNew.error.openingPasswordSuperuserOnly':
    'لم يعد بإمكانك تعيين كلمة مرور افتتاحية: هذا الخيار للمشرف الأعلى للمنصّة وحده. ارجع إلى خطوة المشرف واترك الخيار ليُدعى المشرف برابط على بريده.',
} as const satisfies Messages<'tenantNew'>;
